import { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Point } from "tm-shared/types";

import { useOfflineModeSelector } from "@/hooks/redux";
import { UniversalTabs } from "@/shared/ui/UniversalTabs";

import { AboutPointCapabilities } from "../AboutPoint.policy";
import { DebtTab } from "../DebtTab";
import { HistoryTab } from "../HistoryTab";
import { InfoTab } from "../InfoTab";
import { TasksTab } from "../TasksTab";

export type TabValue = "info" | "history" | "debt" | "tasks";

export interface AboutPointTabConfig {
    label: string;
    value: TabValue;
    requiresOnline?: boolean;
    render: () => ReactNode;
}

export const getAboutPointTabs = (
    capabilities: AboutPointCapabilities,
    point: Point,
): AboutPointTabConfig[] => {
    const allTabs: (AboutPointTabConfig & { isVisible: boolean })[] = [
        {
            label: "Інфо",
            value: "info",
            isVisible: true,
            render: () => (
                <InfoTab point={point} capabilities={capabilities.infoTab} />
            ),
        },
        {
            label: "Історія",
            value: "history",
            isVisible: capabilities.canSeeHistoryTab,
            requiresOnline: true,
            render: () => <HistoryTab pointId={point.id} />,
        },
        {
            label: "Дебіторка",
            value: "debt",
            isVisible: capabilities.canSeeDebtTab,
            requiresOnline: true,
            render: () => <DebtTab hasDebt={point.hasDebt} />,
        },
        {
            label: "Задачі",
            value: "tasks",
            isVisible: true,
            requiresOnline: true,
            render: () => (
                <TasksTab point={point} capabilities={capabilities.tasksTab} />
            ),
        },
    ];

    return allTabs.filter((t) => t.isVisible);
};

export const TabsBlock = ({
    capabilities,
    point,
}: {
    capabilities: AboutPointCapabilities;
    point: Point;
}) => {
    const offlineMode = useOfflineModeSelector();
    const [searchParams, setSearchParams] = useSearchParams();

    const tabs = getAboutPointTabs(capabilities, point);

    const configuredTabs = tabs.map((t) => ({
        ...t,
        disabled: offlineMode && t.requiresOnline,
    }));

    const fromQuery = (searchParams.get("tab") || "").toLowerCase() as TabValue;

    const activeTab = configuredTabs.some((t) => t.value === fromQuery)
        ? fromQuery
        : "info";

    const setTab = (next: TabValue) => {
        const targetTab = configuredTabs.find((t) => t.value === next);
        if (targetTab && !targetTab.disabled) {
            setSearchParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    sp.set("tab", next);
                    return sp;
                },
                { replace: true },
            );
        }
    };

    const activeTabConfig = configuredTabs.find((t) => t.value === activeTab);

    return (
        <>
            <UniversalTabs
                tabs={configuredTabs}
                onChange={setTab}
                value={activeTab}
            />
            {activeTabConfig?.render()}
        </>
    );
};
